import React, { useState, useEffect } from 'react';
import { 
  Megaphone, 
  AlertTriangle, 
  X, 
  Check,
  Users,
  Clock
} from 'lucide-react';
import { EmergencyBroadcast as BroadcastType, SeverityLevel } from '../types';
import { SocketManager } from '../utils/socketManager';

interface EmergencyBroadcastProps {
  currentUserId: string;
  userType: 'dispatcher' | 'responder';
  onBroadcastCreate?: (broadcast: BroadcastType) => void;
  className?: string;
}

export const EmergencyBroadcast: React.FC<EmergencyBroadcastProps> = ({
  currentUserId,
  userType,
  onBroadcastCreate,
  className = ''
}) => {
  const [broadcasts, setBroadcasts] = useState<BroadcastType[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newBroadcast, setNewBroadcast] = useState({
    title: '',
    message: '',
    severity: 'medium' as SeverityLevel,
    targetAudience: 'all' as BroadcastType['targetAudience']
  });
  const [socketManager] = useState(() => new SocketManager());

  useEffect(() => {
    socketManager.onEmergencyBroadcast((broadcast) => {
      setBroadcasts(prev => [broadcast, ...prev]);
      
      // Show notification for new broadcasts
      if (broadcast.targetAudience === 'all' || broadcast.targetAudience === userType + 's') {
        showBroadcastNotification(broadcast);
      }
    });

    // Load existing broadcasts (in real app, this would come from server)
    const mockBroadcasts: BroadcastType[] = [
      {
        id: '1',
        title: 'System Maintenance',
        message: 'Emergency response system will undergo maintenance from 2:00 AM to 4:00 AM.',
        severity: 'low',
        targetAudience: 'all',
        timestamp: new Date(Date.now() - 3600000),
        isActive: true,
        acknowledgedBy: []
      }
    ];
    setBroadcasts(mockBroadcasts);

    return () => {
      socketManager.disconnect();
    };
  }, [userType]);

  const showBroadcastNotification = (broadcast: BroadcastType) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`Emergency Broadcast: ${broadcast.title}`, {
        body: broadcast.message,
        icon: '/emergency-icon.png'
      });
    }
  };

  const handleCreateBroadcast = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newBroadcast.title.trim() || !newBroadcast.message.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const broadcast: BroadcastType = {
      id: Date.now().toString(),
      ...newBroadcast,
      timestamp: new Date(),
      isActive: true,
      acknowledgedBy: []
    };

    setBroadcasts(prev => [broadcast, ...prev]);
    socketManager.sendEmergencyBroadcast(broadcast);
    onBroadcastCreate?.(broadcast);

    // Reset form
    setNewBroadcast({
      title: '',
      message: '',
      severity: 'medium',
      targetAudience: 'all'
    });
    setShowCreateForm(false);
  };

  const handleAcknowledgeBroadcast = (broadcastId: string) => {
    setBroadcasts(prev => prev.map(broadcast => 
      broadcast.id === broadcastId
        ? { ...broadcast, acknowledgedBy: [...broadcast.acknowledgedBy, currentUserId] }
        : broadcast
    ));
  };

  const getSeverityColor = (severity: SeverityLevel) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 border-red-500 text-red-800';
      case 'high':
        return 'bg-red-50 border-red-400 text-red-700';
      case 'medium':
        return 'bg-yellow-50 border-yellow-400 text-yellow-700';
      case 'low':
        return 'bg-blue-50 border-blue-400 text-blue-700';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString();
  };

  const isAcknowledged = (broadcast: BroadcastType) => {
    return broadcast.acknowledgedBy.includes(currentUserId);
  };

  return (
    <div className={`bg-white rounded-lg shadow-md ${className}`}>
      <div className="p-4 border-b bg-red-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-gray-900">Emergency Broadcasts</h3>
          </div>
          {userType === 'dispatcher' && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              {showCreateForm ? 'Cancel' : 'New Broadcast'}
            </button>
          )}
        </div>
      </div>

      {/* Create Broadcast Form */}
      {showCreateForm && userType === 'dispatcher' && (
        <form onSubmit={handleCreateBroadcast} className="p-4 border-b bg-gray-50">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={newBroadcast.title}
                onChange={(e) => setNewBroadcast(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Broadcast title..."
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={newBroadcast.message}
                onChange={(e) => setNewBroadcast(prev => ({ ...prev, message: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
                placeholder="Broadcast message..."
                maxLength={500}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity
                </label>
                <select
                  value={newBroadcast.severity}
                  onChange={(e) => setNewBroadcast(prev => ({ ...prev, severity: e.target.value as SeverityLevel }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <select
                  value={newBroadcast.targetAudience}
                  onChange={(e) => setNewBroadcast(prev => ({ ...prev, targetAudience: e.target.value as BroadcastType['targetAudience'] }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="all">All Users</option>
                  <option value="responders">Responders Only</option>
                  <option value="dispatchers">Dispatchers Only</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Send Broadcast
            </button>
          </div>
        </form>
      )}

      {/* Broadcasts List */}
      <div className="max-h-96 overflow-y-auto">
        {broadcasts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p>No broadcasts available</p>
          </div>
        ) : (
          <div className="divide-y">
            {broadcasts.map((broadcast) => (
              <div
                key={broadcast.id}
                className={`p-4 border-l-4 ${getSeverityColor(broadcast.severity)} ${
                  isAcknowledged(broadcast) ? 'opacity-75' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{broadcast.title}</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 capitalize">
                      {broadcast.severity}
                    </span>
                    {isAcknowledged(broadcast) && (
                      <Check className="w-4 h-4 text-green-600" title="Acknowledged" />
                    )}
                  </div>
                </div>

                <p className="text-gray-700 text-sm mb-3">{broadcast.message}</p>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatTime(broadcast.timestamp)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span className="capitalize">{broadcast.targetAudience}</span>
                    </div>
                  </div>

                  {!isAcknowledged(broadcast) && (
                    <button
                      onClick={() => handleAcknowledgeBroadcast(broadcast.id)}
                      className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};